/**
 * frontend/src/pages/valuation/_SignDeclarations.tsx
 *
 * VAL-PATH-P7c — the signer's Sign step: the I10-I11 declarations (moved from
 * Intake) and, on a market-only run, the one T7 acknowledgement
 * (_MarketOnlyAck). Both reuse the existing pieces; no new text.
 *
 * Declarations save on the engagement at once (not debounced), so a save can
 * never race Finalize, which reads them from the engagement row. A failed save
 * says so and puts the box back to what is stored. The acknowledgement saves
 * on the draft, the same field Your value and Methodology write.
 */
import { useEffect, useState } from 'react'
import { updateValuationEngagement } from '@/api/valuation'
import type { ValuationEngagement } from '@/types/valuation'
import type { ComputedPayload } from '@/types/valuationB3'
import { formatApiError } from '@/utils/httpErrors'
import { IntakeDeclarationsSection, declarationsFromEngagement, type DeclarationsState } from './_IntakeParticulars'
import { MarketOnlyAck, isMarketOnlyRun } from './_MarketOnlyAck'
import type { UseValuationDraft } from './_valuationDraft'

export function SignDeclarations({
  engagement,
  computed,
  savedAck,
  save,
}: {
  engagement: ValuationEngagement
  computed: ComputedPayload | null | undefined
  savedAck: boolean
  save: UseValuationDraft['save']
}) {
  const id = engagement.valuation_engagement_id
  const [stored, setStored] = useState<DeclarationsState>(() => declarationsFromEngagement(engagement))
  const [shown, setShown] = useState<DeclarationsState>(stored)
  const [problem, setProblem] = useState<string | null>(null)
  const [ack, setAck] = useState(savedAck)

  useEffect(() => { setAck(savedAck) }, [savedAck])

  async function changeDeclarations(next: DeclarationsState) {
    setShown(next)
    setProblem(null)
    try {
      await updateValuationEngagement(id, next)
      setStored(next)
    } catch (err) {
      setShown(stored)
      setProblem(`Not saved — ${formatApiError(err)}`)
    }
  }

  async function changeAck(next: boolean) {
    setAck(next)
    const err = await save({ market_only_acknowledged: next })
    if (err) {
      setAck(!next)
      setProblem(`Not saved — ${err}`)
    }
  }

  return (
    <div className="space-y-2">
      <IntakeDeclarationsSection declarations={shown} setDeclarations={changeDeclarations} />
      {isMarketOnlyRun(computed) && <MarketOnlyAck checked={ack} onChange={changeAck} />}
      {problem && <p role="alert" className="text-xs text-red-700">{problem}</p>}
    </div>
  )
}
